import {PAYMENT_KEYS} from "@/api/paymentApi";
import {useUserPaymentMethodsQuery} from "@/api/userApi";
import InfinityIcon from "@/assets/colorable/infinite.svg?react";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import LogoutIcon from "@/assets/colorable/logout.svg?react";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import BuyoutIcon from "@/assets/colorable/source_content.svg?react";
import TikTokIcon from "@/assets/colorable/tiktok.svg?react";
import UgcIcon from "@/assets/colorable/ugc.svg?react";
import PaymentFailedIcon from "@/assets/pricing/E02B1D_payment_failed.svg?react";
import Button from "@/components/Common/Button";
import Loader from "@/components/Common/kpi/Loader";
import Modal from "@/components/Common/Modal";
import CardForm from "@/components/Common/payment/CardForm";
import TextButton from "@/components/Common/TextButton";
import {useLogout} from "@/hooks/useLogout";
import {getPackageIcon, packages} from "@/hooks/usePackages";
import {usePayment} from "@/hooks/usePayment";
import {usePriceFormat} from "@/hooks/usePriceFormat";
import {useIsAdmin, useSelectedCompanyId, useUserId} from "@/hooks/useUserHooks";
import CardComponent from "@/pages/profile/CardComponent";
import {Can} from "@/permissions";
import {queryClient} from "@/queryClient";
import {CARD_BRANDS} from "@/store/constants";
import {hasClosedPaymentFailedModalAtom, paymentFailedModalDataAtom} from "@/store/jotaiAtoms";
import type {TPackageName} from "@/store/types";
import dayjs from "dayjs";
import {useAtom, useSetAtom} from "jotai";
import {findKey, sumBy} from "lodash";
import {useMemo, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router";

const PaymentFailedModal: FC = () => {
  const {t} = useTranslation();
  const {companyId} = useParams();
  const formatPrice = usePriceFormat();
  const {getTranslatedErrorMessage} = usePayment();
  const isAdmin = useIsAdmin();
  const selectedCompanyId = useSelectedCompanyId();
  const logout = useLogout();
  const setHasClosedPaymentFailedModal = useSetAtom(hasClosedPaymentFailedModalAtom);
  const userId = useUserId();
  const [state, setState] = useAtom(paymentFailedModalDataAtom);
  const [showAddNewPaymentMethodForm, setShowAddNewPaymentMethodForm] = useState(false);
  const [showUpdatePaymentMethodForm, setShowUpdatePaymentMethodForm] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const {payInvoices, invoicePayIsLoading, setDefaultPaymentMethod, setDefaultLoading} = usePayment();
  const currency = useMemo(() => state?.[0]?.currency ?? "CHF", [state]);
  const totalAmount = useMemo(() => sumBy(state, invoice => invoice.amount ?? 0), [state]);
  const shouldHideCloseButton = useMemo(
    () => Math.max(...(state ?? []).map(invoice => dayjs().diff(invoice.created, "days"))) > 10,
    [state],
  );
  const {data: paymentMethods, isPending: isLoadingPaymentMethods} = useUserPaymentMethodsQuery(userId);

  return (
    <Modal
      shouldHideCloseButton={shouldHideCloseButton}
      onClose={() => {
        setState(null);
        setHasClosedPaymentFailedModal(true);
      }}
      customTitleRow={
        <div className="flex items-center gap-2 p-5">
          <PaymentFailedIcon className="h-auto w-6 shrink-0" />
          <span className="text-neutral-black text-xl font-semibold">{t("failedToProcessPayment")}</span>
        </div>
      }
      customBottomRow={
        <div className="border-neutral-grey-light-dark flex flex-wrap items-center gap-4 border-t p-5">
          <div className="flex flex-col gap-1">
            <span className="text-neutral-grey-dark text-xs">{t("amount_due")}</span>
            <span className="text-black-bluetint text-3xl font-semibold">{formatPrice(totalAmount)}</span>
          </div>
          <Button
            onClick={async () => {
              if (!selectedPaymentMethodId) {
                return;
              }

              await setDefaultPaymentMethod({
                paymentMethodId: selectedPaymentMethodId,
              });
              await payInvoices({
                invoiceIds: state?.map(invoice => invoice.id) ?? [],
              });
              setTimeout(() => {
                const params = `company_profile_id=${isAdmin ? companyId : selectedCompanyId}`;
                [PAYMENT_KEYS.qRegularInvoiceList(params), PAYMENT_KEYS.qRegularInvoiceListPdfExport(params)].map(
                  queryKey => queryClient.invalidateQueries({queryKey}),
                );
              }, 2000);
              setSelectedPaymentMethodId(null);
              setState(null);
            }}
            buttonStyle="Gradient"
            isDisabled={!selectedPaymentMethodId || invoicePayIsLoading || setDefaultLoading}
            className="ml-auto min-w-[300px] px-8 py-3">
            {t("pay")}
          </Button>
        </div>
      }
      backgroundClassName="max-w-[840px]">
      <div className="flex flex-col gap-5">
        <span className="text-black-bluetint text-[28px] leading-8 font-semibold">
          {t("whileProcessingYourPaymentFor")}:
        </span>
        <table className="text-neutral-black w-full text-left text-xs">
          <thead className="bg-neutral-grey-lightest text-neutral-grey-dark rounded-md text-xs font-semibold uppercase">
            <tr>
              <th className="border-neutral-white-dark border-y border-l px-3 py-2.5 tracking-wider">{t("item")}</th>
              <th className="border-neutral-white-dark border-y px-3 py-2.5 tracking-wider">{t("Description")}</th>
              <th className="border-neutral-white-dark border-y px-3 py-2.5 tracking-wider">{t("dateBilled")}</th>
              <th className="border-neutral-white-dark border-y border-r px-3 py-2.5 tracking-wider">
                {t("price")} ({currency})
              </th>
            </tr>
          </thead>
          <tbody>
            {state?.map(invoice => {
              const plan =
                invoice.invoice_type === "PACKAGE_SUBSCRIPTION"
                  ? (findKey(packages, {
                      type: invoice.invoice_package_subscription?.name,
                    }) as TPackageName)
                  : null;

              return (
                <tr key={invoice.id} className="border-neutral-white-dark border-b bg-white hover:bg-neutral-100">
                  <td className="p-3">
                    {invoice.invoice_type === "UGC_CAMPAIGN" ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <UgcIcon className="h-5 w-auto shrink-0" />
                        <span className="text-neutral-black text-base font-semibold">
                          {invoice.campaign ? invoice.campaign.title : "-"}
                        </span>
                      </span>
                    ) : invoice.invoice_type == "PACKAGE_SUBSCRIPTION" ? (
                      <span className="flex flex-wrap items-center gap-1">
                        {getPackageIcon(plan!, "h-5 w-auto shrink-0")}
                        <span className="text-neutral-black text-base font-semibold">
                          {t(packages[plan!]?.translationKey)} {t("package")}
                        </span>
                      </span>
                    ) : invoice.invoice_type === "BUNDLE_BUYOUTS" ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <BuyoutIcon className="text-neutral-black size-4" />
                        <span className="text-neutral-black text-base font-semibold">
                          {t("xContentBuyouts", {
                            count: Number(invoice.buyout_count),
                          })}
                        </span>
                      </span>
                    ) : (
                      <span className="flex flex-wrap items-center gap-2">
                        {invoice.campaign?.campaign_type === "Instagram" && (
                          <InstagramIcon className="h-5 w-auto shrink-0" />
                        )}
                        {invoice.campaign?.campaign_type === "TikTok" && <TikTokIcon className="h-5 w-auto shrink-0" />}
                        {invoice.campaign?.on_going && !invoice.campaign.event && (
                          <span className="border-neutral-black h-5 shrink-0 rounded-full border px-2">
                            <InfinityIcon className="h-full w-auto shrink-0" />
                          </span>
                        )}
                        <span className="text-neutral-black text-base font-semibold">
                          {invoice.campaign ? invoice.campaign.title : "-"}
                        </span>
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-2.5 p-3">
                      {invoice.invoice_type === "PACKAGE_SUBSCRIPTION" && (
                        <span className="flex items-center gap-1.5">
                          {(invoice.invoice_package_subscription?.package_category === "INSTAGRAM" ||
                            invoice.invoice_package_subscription?.package_category === "BOTH") && (
                            <InstagramIcon className="size-3 shrink-0" />
                          )}
                          {(invoice.invoice_package_subscription?.package_category === "TIKTOK" ||
                            invoice.invoice_package_subscription?.package_category === "BOTH") && (
                            <TikTokIcon className="size-3 shrink-0" />
                          )}
                        </span>
                      )}
                      {invoice.description}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-wrap items-center gap-1 p-3">
                      <span className="text-sm font-semibold">{dayjs(invoice.created).format("MMM DD, YYYY")}</span>
                    </div>
                  </td>
                  <td className="p-3 text-sm font-semibold">{invoice.amount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="mt-5 flex flex-wrap items-center gap-3 sm:gap-5">
          {paymentMethods?.[0] && (
            <div className="flex items-center gap-3">
              <input
                type="radio"
                checked={selectedPaymentMethodId === paymentMethods[0].id}
                onChange={() => setSelectedPaymentMethodId(paymentMethods[0].id)}
              />
              <div className="flex items-center gap-2 rounded-full border-1 px-3 py-1.5">
                <img
                  src={CARD_BRANDS[
                    paymentMethods[0].type === "card" && paymentMethods[0].card
                      ? paymentMethods[0].card.brand
                      : paymentMethods[0].type
                  ].toLowerCase()}
                  className="h-[18px] shrink-0"
                />
              </div>
              {paymentMethods[0].type === "card" && (
                <span className="text-sm font-medium">**** {paymentMethods[0].card?.last4}</span>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-black-bluetint text-[28px] leading-8 font-semibold">{t("failedBecause")}:</span>
            <div className="border-danger-rubicund bg-danger-light-red text-danger-medium-red flex items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase">
              {getTranslatedErrorMessage(state?.[0]!)}
            </div>
          </div>
        </div>
        {paymentMethods?.[0]?.type === "card" && !showUpdatePaymentMethodForm && (
          <Button
            onClick={() => {
              setShowUpdatePaymentMethodForm(true);
              setShowAddNewPaymentMethodForm(false);
            }}
            buttonStyle="Blue"
            className="h-auto w-fit px-8 py-3">
            {t("updateCardInformation")}
          </Button>
        )}
        {paymentMethods?.[0]?.type === "card" && showUpdatePaymentMethodForm && (
          <CardForm
            buttonLabel={t("updateCardInformation")}
            invoiceIds={state?.map(invoice => invoice.id) ?? []}
            onSubmit={() => setShowUpdatePaymentMethodForm(false)}
            onCancel={() => setShowUpdatePaymentMethodForm(false)}
          />
        )}
        <div className="bg-neutral-grey-light-dark -mx-5 h-px" />

        {isLoadingPaymentMethods ? (
          <Loader />
        ) : paymentMethods?.length! > 1 ? (
          <>
            <span className="text-black-bluetint text-[28px] leading-8 font-semibold">
              {t("use_other_payment_method")}
            </span>
            {paymentMethods
              ?.slice(1)
              ?.map(paymentMethod => (
                <CardComponent
                  key={paymentMethod.id}
                  card={paymentMethod.card}
                  isDeletable={paymentMethods.length > 1}
                  isSelected={selectedPaymentMethodId === paymentMethod.id}
                  onSelect={() => setSelectedPaymentMethodId(paymentMethod.id)}
                  paymentMethodId={paymentMethod.id}
                  showButtons
                  type={paymentMethod.type}
                />
              ))}
          </>
        ) : null}
      </div>

      {showAddNewPaymentMethodForm ? (
        <CardForm
          invoiceIds={state?.map(invoice => invoice.id) ?? []}
          buttonLabel={t("add_new_payment_method")}
          onSubmit={() => setShowAddNewPaymentMethodForm(false)}
          onCancel={() => setShowAddNewPaymentMethodForm(false)}
        />
      ) : (
        <Button
          onClick={() => {
            setShowUpdatePaymentMethodForm(false);
            setShowAddNewPaymentMethodForm(true);
          }}
          buttonStyle="Blue"
          className="h-auto w-fit gap-3 px-8 py-3 sm:min-w-[360px]">
          <PlusIcon className="size-3 shrink-0" /> {t("add_new_payment_method")}
        </Button>
      )}
      {shouldHideCloseButton && (
        <div className="mt-6 flex flex-col gap-6">
          <Button
            buttonStyle="Blue"
            className="h-auto w-fit gap-2 px-8 py-3"
            onClick={() => {
              logout();
              setState(null);
            }}>
            <LogoutIcon />
            {t("clickToLogout")}
          </Button>
          <Can I="create" a="MailToSupport">
            <span className="text-neutral-black text-center text-sm">
              {t("needHelp")} {t("contactOurAdministrator")}:{" "}
              <TextButton
                onClick={() => (window.location.href = `mailto:info@refluenced.com?subject=${t("payment_failed")}`)}
                text={t("Refluenced Support")}
              />
            </span>
          </Can>
        </div>
      )}
    </Modal>
  );
};

export default PaymentFailedModal;
